import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  DirectionEnum,
  ICourseStationInfo,
  resolveAvailabilityRideStationsPreview,
} from "@ryde/shared";

import { FramerTooltip } from "../tooltips/framer-tooltip";
import {
  AvailabilityDirectionLabelStyled,
  AvailabilityDirectionTooltipContent,
} from "./availability-direction-label.styled";

interface IAvailabilityDirectionLabelProps {
  scheduleDirection?: string | null;
  courseDirectionTemplate?: DirectionEnum | string | null;
  label: string;
  stationsInfo?: ICourseStationInfo[] | null;
}

export const AvailabilityDirectionLabel: FC<
  IAvailabilityDirectionLabelProps
> = ({ scheduleDirection, courseDirectionTemplate, label, stationsInfo }) => {
  const { t } = useTranslation();

  const stationsPreview = useMemo(
    () =>
      scheduleDirection
        ? resolveAvailabilityRideStationsPreview({
            stationsInfo,
            scheduleDirection,
            courseDirectionTemplate,
          })
        : undefined,
    [courseDirectionTemplate, scheduleDirection, stationsInfo],
  );

  const tooltipContent = useMemo(() => {
    if (!stationsPreview) {
      return undefined;
    }

    const isPickUp =
      stationsPreview.productionDirection === DirectionEnum.PickUp;
    const timingHint = isPickUp
      ? t("course.AVAILABILITY_TIMING_FINAL_STATION")
      : t("course.AVAILABILITY_TIMING_START_STATION");

    return (
      <AvailabilityDirectionTooltipContent>
        <span>
          {t("course.AVAILABILITY_DIRECTION_TIME_STATION_TOOLTIP_TIME", {
            timeStation: stationsPreview.timeStationName,
          })}
        </span>
        <span>
          {t("course.AVAILABILITY_DIRECTION_TIME_STATION_TOOLTIP_ROUTE", {
            origin: stationsPreview.originStationName,
            destination: stationsPreview.destinationStationName,
          })}
        </span>
        <span>({timingHint})</span>
      </AvailabilityDirectionTooltipContent>
    );
  }, [stationsPreview, t]);

  const tooltipAriaLabel = useMemo(() => {
    if (!stationsPreview) {
      return undefined;
    }

    const isPickUp =
      stationsPreview.productionDirection === DirectionEnum.PickUp;

    return t("course.AVAILABILITY_DIRECTION_TIME_STATION_TOOLTIP", {
      timeStation: stationsPreview.timeStationName,
      origin: stationsPreview.originStationName,
      destination: stationsPreview.destinationStationName,
      timingHint: isPickUp
        ? t("course.AVAILABILITY_TIMING_FINAL_STATION")
        : t("course.AVAILABILITY_TIMING_START_STATION"),
    });
  }, [stationsPreview, t]);

  return (
    <AvailabilityDirectionLabelStyled>
      <span>{label}</span>
      {tooltipContent && (
        <FramerTooltip content={tooltipContent}>
          <span
            className="availability-direction-label__hint-indicator"
            aria-label={tooltipAriaLabel}
          >
            *
          </span>
        </FramerTooltip>
      )}
    </AvailabilityDirectionLabelStyled>
  );
};
